import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Browser} from '@capacitor/browser';
import {UserBearerToken} from '../../../shared/responses/bearer-token';
import {SessionService} from '../../../core/services/session.service';
import {API_BASE_URL, CURRENT_PLATFORM} from '../../../core/services/env.service';
import {Platform} from '../../../core/models/platform.enum';
import {AsideService} from '../../../core/services/aside.service';
import {TitleBreadcrumbsChangedAction, TitleChangedAction} from '../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {FormsModule} from '@angular/forms';
import {JsonPipe, NgClass} from '@angular/common';
import {SpinnerComponent} from '../../components/spinner/spinner.component';

@Component({
  selector: 'motn-admin-tools',
  templateUrl: './admin-tools.component.html',
  styleUrls: ['./admin-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    JsonPipe,
    NgClass,
    SpinnerComponent,
  ],
})
export class AdminToolsComponent implements OnInit {

  private readonly IMPERSONATION_HISTORY_KEY = 'admin-tools-impersonation-history';

  formImpersonateUser: string;
  formChangeOwnerObjectType: string = 'hub';
  formChangeOwnerObjectId: string;
  formChangeOwnerOldId: string;
  formChangeOwnerNewId: string;
  formUserLookupUsername: string;
  userLookupResult: any;
  changeOwnerSuccess: boolean = false;
  changeOwnerError: any = null;
  isLoadingUserLookup: boolean = false;
  isLoadingChangeOwner: boolean = false;
  isLoadingImpersonate: boolean = false;
  impersonateError: any = null;
  private impersonationHistory: string[] = [];


  constructor(
    private asideService: AsideService,
    private store: Store<AppState>,
    private session: SessionService,
  ) {
  }


  ngOnInit(): void {
    this.updateTitle();
    this.updateAside();
    this.loadImpersonationHistory();
  }


  submitImpersonateUser() {
    this.isLoadingImpersonate = true;
    this.impersonateError = null;

    this.session.http.post<UserBearerToken>(
      `${API_BASE_URL}/x-internal-tools/impersonation-tokens`,
      JSON.stringify({
        Username: this.formImpersonateUser,
        Impersonate: true,
      }),
    ).subscribe(
      (data) => {
        const url = encodeURI(`https://emulate.motion.cloud/login?emulate=${JSON.stringify(data)}`);

        // Save to history on successful impersonation
        this.saveToImpersonationHistory(this.formImpersonateUser);
        this.isLoadingImpersonate = false;

        if (CURRENT_PLATFORM === Platform.WEB) {
          window.open(url, `_blank`);
        } else {
          Browser.open({
            url: url,
          });
        }
      },
      (error) => {
        this.impersonateError = error?.error || error;
        this.isLoadingImpersonate = false;
      },
    );
  }


  submitChangeOwner() {
    this.changeOwnerSuccess = false;
    this.changeOwnerError = null;
    this.isLoadingChangeOwner = true;

    this.session.http.post(
      `${API_BASE_URL}/x-internal-tools/owner-changes`,
      JSON.stringify({
        ObjectID: this.formChangeOwnerObjectId,
        ObjectType: this.formChangeOwnerObjectType,
        Owner: {
          OldID: this.formChangeOwnerOldId,
          NewID: this.formChangeOwnerNewId,
        },
      }),
    ).subscribe(
      () => {
        this.changeOwnerSuccess = true;
        this.changeOwnerError = null;
        this.isLoadingChangeOwner = false;
      },
      (error) => {
        this.changeOwnerSuccess = false;
        this.changeOwnerError = error?.error || error;
        this.isLoadingChangeOwner = false;
      },
    );
  }


  submitUserLookup() {
    this.userLookupResult = null;
    this.isLoadingUserLookup = true;

    this.session.http.get(
      `${API_BASE_URL}/users?Username=${encodeURIComponent(this.formUserLookupUsername)}`,
    ).subscribe(
      (data) => {
        this.userLookupResult = data;
        this.isLoadingUserLookup = false;
      },
      (error) => {
        this.userLookupResult = {error: error?.error || error};
        this.isLoadingUserLookup = false;
      },
    );
  }


  private updateTitle() {
    this.store.dispatch(new TitleChangedAction(
      'wrench',
      'Admin Tools',
    ));

    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [
        {
          text: 'Admin Tools',
        },
      ],
    ));
  }


  private updateAside() {
    this.asideService.replaceContextSections([
      // {
      //   name: 'Admin Tools',
      //   items: [],
      // },
    ]);
  }


  private loadImpersonationHistory(): void {
    try {
      const stored = localStorage.getItem(this.IMPERSONATION_HISTORY_KEY);
      if (stored) {
        this.impersonationHistory = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load impersonation history:', error);
      this.impersonationHistory = [];
    }
  }


  private saveToImpersonationHistory(username: string): void {
    if (!username) {
      return;
    }

    // Remove if already exists (to move to front)
    this.impersonationHistory = this.impersonationHistory.filter(u => u !== username);

    // Add to front
    this.impersonationHistory.unshift(username);

    // Save to localStorage
    try {
      localStorage.setItem(this.IMPERSONATION_HISTORY_KEY, JSON.stringify(this.impersonationHistory));
    } catch (error) {
      console.error('Failed to save impersonation history:', error);
    }
  }

}
